import { useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '../supabaseClient'
import { UserConsent, TermsVersion, isoDate } from '@repo/domain'

interface TermsConsentState {
  loading: boolean
  requiresConsent: boolean
  termsVersion: TermsVersion | null
  accept: () => Promise<void>
}

export function useTermsConsent(user: User | null): TermsConsentState {
  const [loading, setLoading] = useState(true)
  const [requiresConsent, setRequiresConsent] = useState(false)
  const [termsVersion, setTermsVersion] = useState<TermsVersion | null>(null)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setRequiresConsent(false)
      setTermsVersion(null)
      return
    }

    checkConsent(user)
  }, [user?.id])

  const checkConsent = async (user: User) => {
    setLoading(true)
    try {
      const { data } = await (supabase as any)
        .from('user_terms_consents')
        .select('accepted_at')
        .eq('user_id', user.id)
        .maybeSingle()

      const registeredAt = isoDate(user.created_at.split('T')[0])
      const acceptedAt = data?.accepted_at ? isoDate(data.accepted_at.split('T')[0]) : null

      const consent = new UserConsent({ registeredAt, acceptedAt })
      setRequiresConsent(consent.requiresConsent)
      setTermsVersion(consent.requiredVersion)
    } finally {
      setLoading(false)
    }
  }

  const accept = async () => {
    if (!user || !termsVersion) return

    const { error } = await (supabase as any)
      .from('user_terms_consents')
      .insert({ user_id: user.id, terms_version: termsVersion })

    if (error) throw error

    setRequiresConsent(false)
  }

  return { loading, requiresConsent, termsVersion, accept }
}
